import { useEffect, useMemo, useRef, useState } from "react";
import axios from "../../services/axios";
import Constants from "../../constants/Index";
import { useEditOnboarding } from "../../hooks/useEditOnboarding";
import { DropdownV2, FormCardV2, FormGridV2, MultiDropdownV2, OptionV2 } from "../FormControlsV2";
import { FooterEditOnboarding } from "../EditOnboarding/Footer";

type ReportFormValue = {
  farm: OptionV2 | null;
  areas: OptionV2[];
  microAreas: OptionV2[];
  model: OptionV2<number> | null;
};

type ReportFormErrors = Partial<Record<keyof ReportFormValue, string>>;

const reportModelOptions: OptionV2<number>[] = [
  { value: 1, label: "Relatório global" },
  { value: 2, label: "Relatório por pasto" },
];

const emptyReportValue: ReportFormValue = {
  farm: null,
  areas: [],
  microAreas: [],
  model: null,
};

function toIdSet(values: Array<string | number | undefined>) {
  return new Set(values.filter((value) => value !== undefined).map((value) => String(value)));
}

export function PastureEvaluationReportFormV2() {
  const { setOptionForm } = useEditOnboarding();
  const [farms, setFarms] = useState<OptionV2[]>([]);
  const [availableFarmIds, setAvailableFarmIds] = useState<Set<string>>(new Set());
  const [areas, setAreas] = useState<OptionV2[]>([]);
  const [availableAreaIds, setAvailableAreaIds] = useState<Set<string>>(new Set());
  const [microAreas, setMicroAreas] = useState<OptionV2[]>([]);
  const [availableMicroAreaIds, setAvailableMicroAreaIds] = useState<Set<string>>(new Set());
  const [value, setValue] = useState<ReportFormValue>(emptyReportValue);
  const [errors, setErrors] = useState<ReportFormErrors>({});
  const [renderUrl, setRenderUrl] = useState("");
  const [renderFilterKey, setRenderFilterKey] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [loading, setLoading] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const availableFarms = useMemo(
    () => farms.filter((farm) => availableFarmIds.has(String(farm.value))),
    [availableFarmIds, farms],
  );

  const filteredAreas = useMemo(
    () =>
      areas
        .filter((area) => String(area.info?.farm?.id) === String(value.farm?.value))
        .filter((area) => availableAreaIds.has(String(area.value)))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [areas, availableAreaIds, value.farm],
  );

  const filteredMicroAreas = useMemo(
    () =>
      microAreas
        .filter((microArea) =>
          value.areas.some((area) => String(area.value) === String(microArea.info?.area_id)),
        )
        .filter((microArea) => availableMicroAreaIds.has(String(microArea.value)))
        .sort((a, b) => String(a.value).localeCompare(String(b.value))),
    [availableMicroAreaIds, microAreas, value.areas],
  );

  function updateValue(nextValue: ReportFormValue) {
    setValue(nextValue);
    setRenderUrl("");
    setRenderFilterKey("");
    setSubmitError("");
    if (Object.keys(errors).length) validate(nextValue);
  }

  function validate(nextValue = value) {
    const nextErrors: ReportFormErrors = {};
    if (!nextValue.farm) nextErrors.farm = "Fazenda é necessária.";
    if (!nextValue.areas.length) nextErrors.areas = "Retiros são necessários.";
    if (!nextValue.microAreas.length) nextErrors.microAreas = "Pastos são necessários.";
    if (!nextValue.model) nextErrors.model = "Modelo é necessário.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  function buildReportParams() {
    const reportParams = new URLSearchParams();
    const reportModel = value.model?.value === 1 ? "GLOBAL" : "MICRO_AREA";
    reportParams.set("selected_farm_id", String(value.farm?.value));
    value.areas.forEach((area) => reportParams.append("area_ids", String(area.value)));
    value.microAreas.forEach((microArea) => reportParams.append("micro_area_ids", String(microArea.value)));
    reportParams.set("model", reportModel);
    reportParams.set("print", "1");

    const filterKey = reportParams.toString();
    reportParams.set("requested_at", String(Date.now()));
    return { filterKey, reportParams };
  }

  function requestReports() {
    if (!validate()) return;

    const { filterKey, reportParams } = buildReportParams();
    const previewWindow = iframeRef.current?.contentWindow;

    if (renderUrl && renderFilterKey === filterKey && previewWindow) {
      previewWindow.focus();
      previewWindow.print();
      return;
    }

    setSubmitError("");
    setLoading(true);
    setRenderFilterKey(filterKey);

    setRenderUrl(`${Constants.PASTURE_EVALUATION_REPORT_RENDER}?${reportParams.toString()}`);
  }

  useEffect(() => {
    axios.get("/farms/user").then((response) => {
      setFarms(response.data.map((farm: any) => ({ value: farm.id, label: farm.name, info: farm })));
    });

    axios.get("/areas/user").then((response) => {
      setAreas(response.data.map((area: any) => ({ value: area.id, label: area.name, info: area })));
    });

    axios.get("/tasks/pasture-evaluation/micro-areas").then(({ data }) => {
      setAvailableAreaIds(toIdSet(data.map((microArea: any) => microArea?.area_id)));
      setAvailableFarmIds(toIdSet(data.map((microArea: any) => microArea?.farm_id)));
      setAvailableMicroAreaIds(toIdSet(data.map((microArea: any) => microArea?.id)));
      setMicroAreas(
        data.map((microArea: any) => ({
          label: `${microArea?.farm_initials}-${microArea?.area_initials}-${microArea?.initials}`,
          value: microArea?.id,
          info: {
            farm_id: microArea?.farm_id,
            area_id: microArea?.area_id,
          },
        })),
      );
    });
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = Constants.TITLE_PASTURE_EVALUATION;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    if (value.model) document.title = value.model.label;
  }, [value.model]);

  useEffect(() => {
    function handleReportMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === "PASTEJO_REPORT_READY") setLoading(false);
      if (event.data?.type === "PASTEJO_REPORT_ERROR") {
        setLoading(false);
        setSubmitError("Não foi possível renderizar o relatório.");
      }
    }

    window.addEventListener("message", handleReportMessage);
    return () => window.removeEventListener("message", handleReportMessage);
  }, []);

  return (
    <>
      <main className="body__formEditOnboarding pasture-evaluation-report-v2">
        <div className="pasture-evaluation-report-v2__layout">
          <div className="pasture-evaluation-report-v2__form no-print">
            <FormCardV2>
              <FormGridV2 columns={1}>
                <DropdownV2
                  name="farm"
                  label="Fazenda"
                  value={value.farm}
                  options={availableFarms}
                  placeholder="Selecionar fazenda..."
                  searchable
                  error={errors.farm}
                  emptyMessage="Nenhuma fazenda disponível"
                  onChange={(farm) => updateValue({ ...value, farm, areas: [], microAreas: [] })}
                />

                <MultiDropdownV2
                  name="areas"
                  label="Retiros"
                  value={value.areas}
                  options={filteredAreas}
                  placeholder="Selecionar retiros..."
                  searchable
                  disabled={!value.farm}
                  error={errors.areas}
                  emptyMessage="Nenhum retiro disponível"
                  onChange={(selectedAreas) => {
                    const selectedMicroAreas = microAreas
                      .filter((microArea) =>
                        selectedAreas.some((area) => String(area.value) === String(microArea.info?.area_id)),
                      )
                      .filter((microArea) => availableMicroAreaIds.has(String(microArea.value)))
                      .sort((a, b) => String(a.value).localeCompare(String(b.value)));

                    updateValue({ ...value, areas: selectedAreas, microAreas: selectedMicroAreas });
                  }}
                />

                <MultiDropdownV2
                  name="microareas"
                  label="Pastos"
                  value={value.microAreas}
                  options={filteredMicroAreas}
                  placeholder="Selecionar pastos..."
                  searchable
                  disabled={!value.areas.length}
                  error={errors.microAreas}
                  emptyMessage="Nenhum pasto disponível"
                  onChange={(selectedMicroAreas) => updateValue({ ...value, microAreas: selectedMicroAreas })}
                />

                <DropdownV2
                  name="model"
                  label="Modelo"
                  value={value.model}
                  options={reportModelOptions}
                  placeholder="Selecionar modelo..."
                  error={errors.model}
                  emptyMessage="Nenhum modelo disponível"
                  onChange={(model) => updateValue({ ...value, model })}
                />
              </FormGridV2>
            </FormCardV2>
          </div>

          <div className="pasture-evaluation-report-v2__preview no-print">
            {renderUrl ? (
              <iframe
                ref={iframeRef}
                className="pasture-evaluation-report-v2__iframe"
                src={renderUrl}
                title="Pré-visualização do relatório"
              />
            ) : (
              <div className="pasture-evaluation-report-v2__preview-placeholder">
                <strong>Pré-visualização da avaliação</strong>
                <span>Selecione os filtros e clique em Exportar para exportar a avaliação.</span>
                {submitError && <small>{submitError}</small>}
              </div>
            )}
            {loading && (
              <div className="pasture-evaluation-report-v2__preview-loading">
                <div className="lds-ring">
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <FooterEditOnboarding
        undoFunction={() => setOptionForm(undefined)}
        saveFunction={requestReports}
        isNew
        saveLabel="Exportar"
      />

    </>
  );
}
